/**
 * @engine/input — Input Backend
 *
 * Abstract interface for platform-specific input sources.
 * Web: browser events (keyboard, mouse, gamepad API, touch, pointer, sensors)
 * Native: Silk.NET or similar (injected into global)
 */

import type { DeviceId, DeviceType, DeviceInfo, InputEvent } from '../types';

/**
 * Callback for input events
 */
export type InputEventCallback = (event: InputEvent) => void;

/**
 * Callback for device connection events
 */
export type DeviceCallback = (info: DeviceInfo) => void;

/**
 * Abstract input backend interface.
 * Implementations connect to platform-specific input sources.
 */
export interface IInputBackend {
  /** Backend name for debugging */
  readonly name: string;

  /** Is this backend currently active */
  readonly isActive: boolean;

  /**
   * Initialize the backend.
   * Called once at startup.
   */
  initialize(): Promise<void>;

  /**
   * Shutdown the backend.
   * Clean up resources, remove event listeners.
   */
  shutdown(): void;

  /**
   * Poll for input updates (called each frame).
   * Some backends need explicit polling (e.g., Gamepad API).
   */
  poll(time: number): void;

  /**
   * Get currently connected devices
   */
  getConnectedDevices(): DeviceInfo[];

  /**
   * Subscribe to input events
   */
  onInputEvent(callback: InputEventCallback): () => void;

  /**
   * Subscribe to device connect events
   */
  onDeviceConnected(callback: DeviceCallback): () => void;

  /**
   * Subscribe to device disconnect events
   */
  onDeviceDisconnected(callback: DeviceCallback): () => void;

  /**
   * Request pointer lock (for mouse capture in games)
   */
  requestPointerLock?(): Promise<boolean>;

  /**
   * Exit pointer lock
   */
  exitPointerLock?(): void;

  /**
   * Check if pointer is locked
   */
  isPointerLocked?(): boolean;

  /**
   * Request fullscreen
   */
  requestFullscreen?(): Promise<boolean>;

  /**
   * Trigger haptic feedback (rumble)
   */
  triggerHaptic?(deviceId: DeviceId, intensity: number, duration: number): void;
}

/**
 * Base implementation with common logic
 */
export abstract class InputBackend implements IInputBackend {
  abstract readonly name: string;

  protected _isActive = false;

  protected eventCallbacks = new Set<InputEventCallback>();
  protected connectCallbacks = new Set<DeviceCallback>();
  protected disconnectCallbacks = new Set<DeviceCallback>();

  get isActive(): boolean {
    return this._isActive;
  }

  abstract initialize(): Promise<void>;
  abstract shutdown(): void;
  abstract poll(time: number): void;
  abstract getConnectedDevices(): DeviceInfo[];

  onInputEvent(callback: InputEventCallback): () => void {
    this.eventCallbacks.add(callback);
    return () => this.eventCallbacks.delete(callback);
  }

  onDeviceConnected(callback: DeviceCallback): () => void {
    this.connectCallbacks.add(callback);
    return () => this.connectCallbacks.delete(callback);
  }

  onDeviceDisconnected(callback: DeviceCallback): () => void {
    this.disconnectCallbacks.add(callback);
    return () => this.disconnectCallbacks.delete(callback);
  }

  // ============================================================================
  // Optional capabilities (subclasses can override)
  // ============================================================================

  /**
   * Request pointer lock (mouse capture for FPS games)
   */
  requestPointerLock(): Promise<boolean> {
    return Promise.resolve(false);
  }

  /**
   * Exit pointer lock
   */
  exitPointerLock(): void {
    // No-op by default
  }

  /**
   * Check if pointer is locked
   */
  isPointerLocked(): boolean {
    return false;
  }

  /**
   * Trigger haptic feedback (rumble)
   */
  triggerHaptic(_deviceId: DeviceId, _intensity: number, _duration: number): void {
    // No-op by default
  }

  // ============================================================================
  // Protected helpers for implementations
  // ============================================================================

  protected emitEvent(event: InputEvent): void {
    for (const cb of this.eventCallbacks) {
      cb(event);
    }
  }

  protected emitDeviceConnected(info: DeviceInfo): void {
    for (const cb of this.connectCallbacks) {
      cb(info);
    }
  }

  protected emitDeviceDisconnected(info: DeviceInfo): void {
    for (const cb of this.disconnectCallbacks) {
      cb(info);
    }
  }
}
